/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAppearanceForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for managing appearance form with react-hook-form.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useThemeStore } from '../../../store/theme';
import { appearanceSchema } from '../types';
import { getDefaultAppearanceValues } from '../utils';
import type { AppearanceForm } from '../types';

/**
 * Custom hook for managing appearance form
 */
export function useAppearanceForm() {
  const { themePrefs, updateThemePrefsLocal, isLoading } = useThemeStore();

  // Use refs to avoid dependency issues and prevent loops
  const updateThemePrefsLocalRef = useRef(updateThemePrefsLocal);
  const isInitializedRef = useRef(false);
  const lastUpdateRef = useRef<string>('');

  // Keep ref updated
  useEffect(() => {
    updateThemePrefsLocalRef.current = updateThemePrefsLocal;
  }, [updateThemePrefsLocal]);

  const prevIsLoadingRef = useRef(isLoading);
  const form = useForm<AppearanceForm>({
    resolver: zodResolver(appearanceSchema),
    defaultValues: getDefaultAppearanceValues(themePrefs)
  });

  // Re-sync form specifically when server load finishes (isLoading transitions true -> false)
  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) {
      if (!form.formState.isDirty) {
        form.reset(getDefaultAppearanceValues(themePrefs));
      }
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, themePrefs, form]);

  const { watch } = form;

  // Watch specific fields individually to avoid unnecessary re-renders
  const mode = watch('mode');
  const darkModeVariant = watch('darkModeVariant');
  const lightModeVariant = watch('lightModeVariant');
  const primaryColor = watch('primaryColor');
  const secondaryColor = watch('secondaryColor');
  const tooltips = watch('tooltips');
  const widget = watch('widget');
  const widgetDark = watch('widgetDark');
  const widgetLight = watch('widgetLight');
  const gridRowHeight = watch('gridRowHeight');
  const gridColumns = watch('gridColumns');
  const borderRadius = watch('borderRadius');
  const notifications = watch('notifications');

  // Memoize watchedValues to prevent unnecessary re-renders
  const watchedValues = useMemo(() => ({
    mode,
    darkModeVariant,
    lightModeVariant,
    primaryColor,
    secondaryColor,
    tooltips,
    widget,
    widgetDark,
    widgetLight,
    gridRowHeight,
    gridColumns,
    borderRadius,
    notifications
  }), [mode, darkModeVariant, lightModeVariant, primaryColor, secondaryColor, tooltips, widget, widgetDark, widgetLight, gridRowHeight, gridColumns, borderRadius, notifications]);

  // Real-time preview: Update localStorage only (not database)
  // Use a stable callback to prevent creating new subscriptions
  const handleFormChange = useCallback((data: Partial<AppearanceForm>) => {
    if (!data || Object.keys(data).length === 0) return;

    // Create a hash of the current data to prevent duplicate updates
    // Create a hash of the current data to prevent duplicate updates
    const dataHash = JSON.stringify({
      mode: data.mode,
      darkModeVariant: data.darkModeVariant,
      lightModeVariant: data.lightModeVariant,
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      widget: data.widget,
      widgetDark: data.widgetDark,
      widgetLight: data.widgetLight,
      gridRowHeight: data.gridRowHeight,
      gridColumns: data.gridColumns,
      borderRadius: data.borderRadius,
      notifications: data.notifications
    });

    // Skip if this is the same data we just updated
    if (lastUpdateRef.current === dataHash) return;
    lastUpdateRef.current = dataHash;

    // Update local storage for real-time preview
    updateThemePrefsLocalRef.current({
      mode: data.mode,
      darkModeVariant: data.darkModeVariant,
      lightModeVariant: data.lightModeVariant,
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      tooltips: data.tooltips,
      widget: data.widget as any,
      widgetDark: data.widgetDark as any,
      widgetLight: data.widgetLight as any,
      gridRowHeight: data.gridRowHeight,
      gridColumns: data.gridColumns,
      borderRadius: data.borderRadius,
      notifications: data.notifications
    });
  }, []);

  useEffect(() => {
    // Mark as initialized after first render
    const initTimeout = setTimeout(() => {
      isInitializedRef.current = true;
    }, 100);

    const subscription = watch((data) => {
      // Only process updates after initialization to avoid initial loop
      if (isInitializedRef.current) {
        handleFormChange(data);
      }
    });

    return () => {
      clearTimeout(initTimeout);
      subscription.unsubscribe();
    };
  }, [watch, handleFormChange]);

  return {
    ...form,
    watchedValues,
    isDirty: form.formState.isDirty
  };
}
